/**
 * Overview — the default landing tab of the Build hub.
 *
 * Port of the structural pieces of `fe-prototype/build/Setup.jsx::Overview`,
 * stripped of the editing affordances and the hardcoded `22/24` test-run
 * score. Phase 2 shows four read-only widgets:
 *
 *   1. Test-run score card — replaced with a Phase-2-honest placeholder
 *      ("Eval coming next"). A parallel agent is shipping the real eval
 *      backend; once that lands, this slot will read those results.
 *   2. Glance card — Voice / Greeting / Hours, populated from the KB
 *      (hours) and stubbed for the not-yet-implemented voice/greeting
 *      settings.
 *   3. KB summary tile — counts derived from the actual `kb_groups`,
 *      with a button that navigates to the Knowledge base tab.
 *   4. What's next list — action items derived from KB state; clicking
 *      an item either navigates to another nav tab or is disabled with
 *      "Coming next" if it depends on a Phase-3 feature.
 */

import { Icon } from "./Icon.js";
import { pickHoursValue } from "./kbHours.js";
import type { KbGroup } from "./types.js";

interface OverviewProps {
  kbGroups: KbGroup[];
  onNavigate: (id: string) => void;
}

/**
 * Surface an extracted opening-hours value for the Overview glance card,
 * or the not-detected sentinel when no hours fact was extracted. The
 * selection logic is shared with the Free-test surface via
 * `pickHoursValue` so the glance and the test answer never disagree.
 */
export function pickHours(kbGroups: KbGroup[]): string {
  return pickHoursValue(kbGroups) ?? "Not detected yet";
}

/**
 * Count KB items by status — `facts` is everything, `toCheck` is
 * `needs_you` items (the prototype's "review" state maps to this in
 * Phase 2), `toAdd` is currently the same as `toCheck` because Phase 2
 * has no separate "gaps" notion distinct from `needs_you` items.
 */
function kbCounts(kbGroups: KbGroup[]): {
  facts: number;
  toCheck: number;
  toAdd: number;
} {
  let facts = 0;
  let needs = 0;
  for (const g of kbGroups) {
    for (const it of g.items) {
      facts++;
      if (it.status === "needs_you") needs++;
    }
  }
  return {
    facts,
    toCheck: 0,
    toAdd: needs,
  };
}

/** Default Overview landing for the Build hub. */
export function Overview({ kbGroups, onNavigate }: OverviewProps) {
  const { facts, toCheck, toAdd } = kbCounts(kbGroups);
  const hoursSummary = pickHours(kbGroups);

  const next: Array<{
    icon: string;
    color: string;
    bg: string;
    text: string;
    to?: string;
    disabled?: boolean;
  }> = [];
  if (toAdd > 0) {
    next.push({
      icon: "circle-help",
      color: "var(--accent)",
      bg: "var(--accent-soft)",
      text: `Fill ${toAdd} missing ${toAdd === 1 ? "detail" : "details"}`,
      to: "knowledge",
    });
  }
  next.push({
    icon: "play-circle",
    color: "var(--ok)",
    bg: "var(--ok-soft)",
    text: "Try the test call",
    disabled: true,
  });

  return (
    <div data-testid="overview-tab">
      <div className="section-head">
        <h2>Your offsider's ready.</h2>
        <p>
          Here's the shape of it. Have a read, take it for a spin when the
          tester lands, then forward your line.
        </p>
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => onNavigate("test")}
            data-testid="overview-test-my-agent"
          >
            test my agent
          </button>
        </div>
      </div>

      <div className="rev-head" style={{ marginBottom: 18 }}>
        <div
          className="score-card score-card-pending"
          style={{ minWidth: 240 }}
          data-testid="overview-score-card"
        >
          <div className="lbl">Test run</div>
          <div className="big" style={{ fontSize: 22, lineHeight: 1.3 }}>
            Eval coming next
          </div>
          <div className="sub">
            We'll run sample calls against your KB and show the score here.
            Coming in the next update.
          </div>
        </div>
        <div
          className="panel ov-card grow"
          style={{ flex: 1, minWidth: 280 }}
          data-testid="overview-glance"
        >
          <div className="ov-glance">
            <div className="ov-line">
              <span className="oi">
                <Icon name="mic" size={15} />
              </span>
              <div>
                <div className="ok-key">Voice</div>
                Default (auto-selected)
              </div>
            </div>
            <div className="ov-line">
              <span className="oi">
                <Icon name="message-circle" size={15} />
              </span>
              <div>
                <div className="ok-key">Greeting</div>
                Auto-generated from your business
              </div>
            </div>
            <div className="ov-line">
              <span className="oi">
                <Icon name="clock" size={15} />
              </span>
              <div>
                <div className="ok-key">Hours</div>
                {hoursSummary}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ov-grid">
        <div className="panel kb-tile" data-testid="overview-kb-tile">
          <div className="panel-h" style={{ padding: "0 0 14px", border: 0 }}>
            <Icon name="book-open" size={18} color="var(--ink-2)" />
            <h3>Knowledge base</h3>
          </div>
          <div className="kb-stat-row">
            <div className="kb-stat">
              <div className="n">{facts}</div>
              <div className="l">facts learned</div>
            </div>
            <div className="kb-stat warn">
              <div className="n">{toCheck}</div>
              <div className="l">to check</div>
            </div>
            <div className="kb-stat add">
              <div className="n">{toAdd}</div>
              <div className="l">to add</div>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={() => onNavigate("knowledge")}
            data-testid="overview-open-kb"
          >
            Open knowledge base
            <Icon name="arrow-right" size={14} />
          </button>
        </div>

        <div className="panel ov-card" data-testid="overview-next-list">
          <div className="panel-h" style={{ padding: "0 0 8px", border: 0 }}>
            <Icon name="list-todo" size={18} color="var(--ink-2)" />
            <h3>What's next</h3>
          </div>
          <div className="next-list">
            {next.map((n, i) => (
              <div
                key={i}
                className={"next-row" + (n.disabled ? " disabled" : "")}
                onClick={() => {
                  if (n.disabled) return;
                  if (n.to) onNavigate(n.to);
                }}
                role={n.disabled ? undefined : "button"}
                aria-disabled={n.disabled}
              >
                <span
                  className="nr-ico"
                  style={{ background: n.bg, color: n.color }}
                >
                  <Icon name={n.icon} size={16} />
                </span>
                <span>{n.text}</span>
                {n.disabled ? (
                  <span className="ni-badge" style={{ marginLeft: "auto" }}>
                    Coming next
                  </span>
                ) : (
                  <span className="nr-go">
                    <Icon name="chevron-right" size={18} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
